import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/providers/language-provider";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  OctagonX,
} from "lucide-react";
import { cn } from "@/lib/utils";

type MessageBoxType =
  | "Confirm"
  | "Confirm_Alert"
  | "Info"
  | "Error"
  | "Warning"
  | "Success";
type MessageBoxResult = "Yes" | "No" | "OK";

type MessageBoxOptions = {
  type?: MessageBoxType; // Optional with default "Confirm"
  title: string;
  description: string;
};

// 각 타입별 설정
const messageBoxConfig = {
  Confirm: {
    icon: AlertCircle,
    iconClass: "text-blue-500 dark:text-blue-400",
    buttonVariant: "default" as const,
    showCancel: true,
  },
  Confirm_Alert: {
    icon: OctagonX,
    iconClass: "text-red-500 dark:text-red-400",
    buttonVariant: "default" as const,
    showCancel: true,
  },
  Info: {
    icon: Info,
    iconClass: "text-slate-500 dark:text-slate-400",
    buttonVariant: "secondary" as const,
    showCancel: false,
  },
  Error: {
    icon: XCircle,
    iconClass: "text-red-500 dark:text-red-400",
    buttonVariant: "destructive" as const,
    showCancel: false,
  },
  Warning: {
    icon: AlertTriangle,
    iconClass: "text-yellow-500 dark:text-yellow-400",
    buttonVariant: "secondary" as const,
    showCancel: false,
  },
  Success: {
    icon: CheckCircle2,
    iconClass: "text-green-500 dark:text-green-400",
    buttonVariant: "secondary" as const,
    showCancel: false,
  },
};

let showMessageBox: (options: MessageBoxOptions) => Promise<MessageBoxResult>;

function MessageBoxComponent() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<MessageBoxOptions | null>(null);
  const [resolvePromise, setResolvePromise] = useState<
    ((value: MessageBoxResult) => void) | null
  >(null);

  showMessageBox = useCallback((options: MessageBoxOptions) => {
    setOptions({
      ...options,
      type: options.type || "Confirm", // Set default type if not provided
    });
    setIsOpen(true);
    return new Promise<MessageBoxResult>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const handleClose = useCallback(
    (result: MessageBoxResult) => {
      setIsOpen(false);
      if (resolvePromise) {
        resolvePromise(result);
      }
    },
    [resolvePromise],
  );

  useEffect(() => {
    return () => {
      if (resolvePromise) {
        resolvePromise("No");
      }
    };
  }, [resolvePromise]);

  if (!isOpen || !options) return null;

  const config = messageBoxConfig[options.type || "Confirm"];
  const Icon = config.icon;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => handleClose(config.showCancel ? "No" : "OK")}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={cn("h-5 w-5", config.iconClass)} />
            {options.title}
          </DialogTitle>
          <DialogDescription className="whitespace-pre-line">
            {options.description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {config.showCancel ? (
            <>
              <Button
                onClick={() => handleClose("Yes")}
                variant={config.buttonVariant}
              >
                {t("Yes")}
              </Button>
              <Button variant="outline" onClick={() => handleClose("No")}>
                {t("No")}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleClose("OK")}
              variant={config.buttonVariant}
            >
              {t("Close")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { MessageBoxComponent, showMessageBox };
